import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = {
  key: 0,
  class: "m-0 flex flex-wrap gap-2 p-0",
  role: "tablist"
}
const _hoisted_3 = ["aria-selected", "data-testid", "onClick"]
const _hoisted_4 = {
  class: "h-4 w-4 shrink-0",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_5 = ["d"]
const _hoisted_6 = { class: "max-w-[160px] truncate" }
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = ["data-testid"]
const _hoisted_9 = {
  class: "h-10 w-10 text-negative",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_10 = ["d"]
const _hoisted_11 = { class: "m-0 text-sm text-textPrimary" }
const _hoisted_12 = { class: "m-0 truncate text-xs text-neutral50" }
const _hoisted_13 = ["data-testid"]
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", { class: "m-0 text-sm text-neutral50" }, "O Registro de Atividade aprovado não tem anexos.", -1)
const _hoisted_15 = [
  _hoisted_14
]
const _hoisted_16 = {
  key: 5,
  class: "flex"
}

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { mdiAlertCircleOutline, mdiFileOutline } from '@mdi/js'

import Network from '@/Network'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import { DSAttendanceStatus } from '@/constants/ds-attendance.constants'
import { pathForStatus } from '@/modules/company/Approvals/DsAttendance/utils/status-route-map'
import type { DSAttendanceActivityAttachment } from '@/types/ds-attendance.types'
import type { StationLegKey } from '@/types/station-evidence.types'
import { getApiErrorMessage } from '@/utils/apiError'


export default /*#__PURE__*/_defineComponent({
  __name: 'StationActivityEvidencePanel',
  props: {
    containerId: {},
    attachments: { default: () => [] },
    leg: {},
    testidPrefix: { default: 'station-evidence' }
  },
  setup(__props: any) {

const props = __props;



const selectedIndex = ref(0)
const isLoading = ref(false)
const loadError = ref<string | null>(null)
const rotation = ref(0)
const zoom = ref(1)

/**
 * Cache por `evidenceId`: voltar a um anexo já aberto não baixa de novo, e a
 * revogação no unmount tem uma lista única para percorrer. O `Map` é SUBSTITUÍDO
 * a cada gravação — mutar in-place não dispara render no Vue 3.
 */
const objectUrls = ref(new Map<number, { url: string; type: string }>())

const currentAttachment = computed(() => props.attachments[selectedIndex.value] ?? null)
const currentEntry = computed(() =>
  currentAttachment.value ? objectUrls.value.get(currentAttachment.value.evidenceId) : undefined
)
const currentUrl = computed(() => currentEntry.value?.url ?? null)
const currentType = computed(() => currentEntry.value?.type)

function revokeAll() {
  objectUrls.value.forEach(({ url }) => URL.revokeObjectURL(url))
  objectUrls.value = new Map()
}

function selectAttachment(index: number) {
  selectedIndex.value = index
  rotation.value = 0
  zoom.value = 1
  void load()
}

async function load() {
  const attachment = currentAttachment.value
  const containerId = props.containerId
  if (!attachment || containerId == null) return
  if (objectUrls.value.has(attachment.evidenceId)) return

  // Mesmo guard dos outros painéis: dois cliques rápidos entre anexos deixariam a
  // resposta antiga apagar o skeleton do anexo atual — ou pior, plantar o erro
  // dela por cima de um anexo que carregou, já que o template prioriza o erro.
  const isCurrent = () =>
    props.containerId === containerId &&
    currentAttachment.value?.evidenceId === attachment.evidenceId

  isLoading.value = true
  loadError.value = null
  try {
    const blob = await Network.dsAttendance.getAttachmentFile(containerId, attachment.evidenceId)
    // A blob URL entra no cache mesmo fora de foco: o arquivo é do anexo certo, e
    // descartá-la aqui vazaria a memória dele.
    const next = new Map(objectUrls.value)
    next.set(attachment.evidenceId, { url: URL.createObjectURL(blob), type: blob.type })
    objectUrls.value = next
  } catch (error) {
    console.error('Erro ao baixar o anexo do Registro de Atividade:', error)
    if (isCurrent()) {
      loadError.value = getApiErrorMessage(error, 'Não foi possível abrir o anexo.')
    }
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}

function openQueue() {
  if (!props.containerId) return
  // A rota sai do mapa canônico, não de uma segunda cópia da regra: só registro
  // APROVADO vira fonte vencedora. Nova aba para não perder o contexto da vaga.
  window.open(
    pathForStatus(props.containerId, DSAttendanceStatus.APPROVED),
    '_blank',
    'noopener,noreferrer'
  )
}

watch(
  () => [props.containerId, props.attachments] as const,
  () => {
    revokeAll()
    selectedIndex.value = 0
    void load()
  },
  { immediate: true }
)

onBeforeUnmount(revokeAll)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "flex flex-col gap-3 text-left",
    "data-testid": `${_ctx.testidPrefix}-attachments-${_ctx.leg}`
  }, [
    (_ctx.attachments.length > 1)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.attachments, (attachment, index) => {
            return (_openBlock(), _createElementBlock("li", {
              key: attachment.evidenceId,
              class: "list-none"
            }, [
              _createElementVNode("button", {
                type: "button",
                role: "tab",
                class: _normalizeClass(["flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors", 
            index === selectedIndex.value
              ? 'border-primary30 bg-primary30 font-semibold text-white'
              : 'border-gray-300 text-textPrimary hover:bg-gray-100'
          ]),
                "aria-selected": index === selectedIndex.value,
                "data-testid": `${_ctx.testidPrefix}-attachment-${attachment.evidenceId}-button`,
                onClick: ($event: any) => (selectAttachment(index))
              }, [
                (_openBlock(), _createElementBlock("svg", _hoisted_4, [
                  _createElementVNode("path", {
                    d: _unref(mdiFileOutline),
                    fill: "currentColor"
                  }, null, 8, _hoisted_5)
                ])),
                _createElementVNode("span", _hoisted_6, _toDisplayString(attachment.originalFileName), 1)
              ], 10, _hoisted_3)
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "min-h-[300px] w-full animate-pulse rounded-lg bg-lightGray30",
          "data-testid": `${_ctx.testidPrefix}-attachments-${_ctx.leg}-loading`
        }, null, 8, _hoisted_7))
      : (loadError.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: "flex min-h-[240px] w-full flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 p-6 text-center",
            "data-testid": `${_ctx.testidPrefix}-attachments-${_ctx.leg}-error`,
            role: "alert"
          }, [
            (_openBlock(), _createElementBlock("svg", _hoisted_9, [
              _createElementVNode("path", {
                d: _unref(mdiAlertCircleOutline),
                fill: "currentColor"
              }, null, 8, _hoisted_10)
            ])),
            _createElementVNode("p", _hoisted_11, _toDisplayString(loadError.value), 1),
            _createVNode(OutlineButton, {
              text: "Tentar novamente",
              width: "200px",
              "data-testid": `${_ctx.testidPrefix}-attachments-${_ctx.leg}-retry-button`,
              onClick: load
            }, null, 8, ["data-testid"])
          ], 8, _hoisted_8))
        : (currentUrl.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createVNode(StepPreview, {
                file: null,
                "file-url": currentUrl.value,
                "file-type": currentType.value,
                rotation: rotation.value,
                zoom: zoom.value,
                mode: "preview",
                "show-display-name": false,
                "onUpdate:rotation": _cache[0] || (_cache[0] = ($event: any) => (rotation.value = $event)),
                "onUpdate:zoom": _cache[1] || (_cache[1] = ($event: any) => (zoom.value = $event))
              }, null, 8, ["file-url", "file-type", "rotation", "zoom"]),
              _createElementVNode("p", _hoisted_12, _toDisplayString(currentAttachment.value?.originalFileName), 1)
            ], 64))
          : (_openBlock(), _createElementBlock("div", {
              key: 4,
              class: "flex min-h-[160px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center",
              "data-testid": `${_ctx.testidPrefix}-attachments-${_ctx.leg}-empty`
            }, _hoisted_15, 8, _hoisted_13)),
    (_ctx.containerId)
      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
          _createVNode(OutlineButton, {
            text: "Abrir na fila de auditoria ↗",
            width: "auto",
            height: "32px",
            "data-testid": `${_ctx.testidPrefix}-open-queue-${_ctx.leg}-button`,
            onClick: openQueue
          }, null, 8, ["data-testid"])
        ]))
      : _createCommentVNode("", true)
  ], 8, _hoisted_1))
}
}

})